import React, { useEffect, useState, useRef } from 'react';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SearchIcon from '@mui/icons-material/Search';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import {
    StyledButton,
    StyledPaper,
    StyledSearchInput,
    StyledList,
    CircularAddIcon,
    StyledAddButton,
} from './RequirementsSelector.styles';
import {
    Box,
    // ClickAwayListener,
    ListItem,
    ListItemIcon,
    ListItemText,
    Stack,
    Tooltip,
    useAppDispatch,
    useTheme,
    useAppSelector,
} from '../../../../../base';
import { UseFieldArrayReturn } from 'react-hook-form';
import {
    SAVE_JOB_FORM_VALUES,
    ISaveJobForm,
    IJobRequirementOption,
    ISaveJobRequirement,
    IServerJobRequirementRecommendation,
    RequirementLevels,
    normalizeRequirementRange,
} from '../../../../../common';
import { useTranslation } from 'react-i18next';
import debounce from 'lodash.debounce';
import { jobsActions, jobsSelectors } from '../../../../../actions';
import PargodaLoading from '../../../../PargodaLoading';

interface Props {
    requirementsArray: UseFieldArrayReturn<
        ISaveJobForm,
        SAVE_JOB_FORM_VALUES.JOB_REQUIREMENTS,
        'requirementId'
    >;
    options: Array<IJobRequirementOption>;
    handleOptionToggle: (option: IJobRequirementOption) => void;
}

const RequirementsSelector = (props: Props) => {
    const { requirementsArray, options, handleOptionToggle } = props;
    const theme = useTheme();
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const [searchTerm, setSearchTerm] = useState('');
    const isRecommendationsLoading = useAppSelector(
        jobsSelectors.getIsJobRequirementsRecommendationsLoading
    );
    const recommendations = useAppSelector(
        jobsSelectors.getActiveJobRequirementRecommendations
    );
    const isRecommendationsExhausted = useAppSelector(
        jobsSelectors.getIsRecommendationsExhausted
    );
    const lastAppliedRecommendationsRef = useRef<string>('');

    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setSearchTerm(value);
    };

    const isRecommendationSelected = (
        recommendation: IServerJobRequirementRecommendation
    ) => {
        return requirementsArray.fields.some((item) => {
            if (
                recommendation.job_requirement_option !== null &&
                String(item.requirementId) ===
                    String(recommendation.job_requirement_option)
            ) {
                return true;
            }
            if (
                item.recommendationId !== null &&
                item.recommendationId !== undefined &&
                String(item.recommendationId) === String(recommendation.id)
            ) {
                return true;
            }
            return false;
        });
    };

    const buildRequirementFromRecommendation = (
        recommendation: IServerJobRequirementRecommendation
    ): ISaveJobRequirement => ({
        requirementId: recommendation.job_requirement_option ?? null,
        recommendationId: recommendation.id,
        name: recommendation.name,
        requirementGroup: recommendation.group_name,
        importanceLevel: RequirementLevels.regular,
        rangeFrom: normalizeRequirementRange(recommendation.range_from),
        rangeTo: normalizeRequirementRange(recommendation.range_to),
        weight: { from: 0, to: 2 },
        isVisible: true,
        rangeInd: true,
        isAIRecommendation: true,
    });

    const areAllRecommendationsSelected = React.useMemo(() => {
        if (!recommendations || recommendations.length === 0) {
            return false;
        }

        return recommendations.every((recommendation) =>
            isRecommendationSelected(recommendation)
        );
    }, [recommendations, requirementsArray.fields]);

    const handleAddRecommendationClicked = () => {
        if (!recommendations || recommendations.length === 0) {
            dispatch(jobsActions.setProceedWithRecommendedModalOpen(true));
            return;
        }

        const unselectedRecommendations = recommendations.filter(
            (recommendation) => !isRecommendationSelected(recommendation)
        );

        const toAppend: ISaveJobRequirement[] = [];
        unselectedRecommendations.forEach((recommendation) => {
            if (recommendation.job_requirement_option !== null) {
                const matchingOption = options.find(
                    (option) =>
                        String(option.id) ===
                        String(recommendation.job_requirement_option)
                );
                if (matchingOption) {
                    handleOptionToggle(matchingOption);
                    return;
                }
            }
            toAppend.push(buildRequirementFromRecommendation(recommendation));
        });

        if (toAppend.length > 0) {
            requirementsArray.append(toAppend);
        }
    };

    const debouncedSearch = debounce((term: string) => {
        dispatch(jobsActions.fetchJobRequirementsOptions({ search: term }));
    }, 500);

    useEffect(() => {
        debouncedSearch(searchTerm);
        return () => {
            debouncedSearch.cancel();
        };
    }, [searchTerm]);

    useEffect(() => {
        if (
            !recommendations ||
            recommendations.length === 0 ||
            isRecommendationsLoading
        ) {
            return;
        }

        const recommendationsHash = recommendations
            .map((r) => r.id)
            .sort()
            .join('-');

        if (lastAppliedRecommendationsRef.current === recommendationsHash) {
            return;
        }

        const unselectedRecommendations = recommendations.filter(
            (recommendation) => !isRecommendationSelected(recommendation)
        );

        if (unselectedRecommendations.length === 0) {
            lastAppliedRecommendationsRef.current = recommendationsHash;
            return;
        }

        const toAppend: ISaveJobRequirement[] = [];
        unselectedRecommendations.forEach((recommendation) => {
            if (recommendation.job_requirement_option !== null) {
                const matchingOption = options.find(
                    (option) =>
                        String(option.id) ===
                        String(recommendation.job_requirement_option)
                );
                if (matchingOption) {
                    handleOptionToggle(matchingOption);
                    return;
                }
            }
            toAppend.push(buildRequirementFromRecommendation(recommendation));
        });

        if (toAppend.length > 0) {
            requirementsArray.append(toAppend);
        }

        lastAppliedRecommendationsRef.current = recommendationsHash;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [recommendations, isRecommendationsLoading]);

    return (
        <Box sx={{ width: 250, position: 'sticky', top: 0 }}>
            <StyledButton>{t('saveJob.addRequirements')}</StyledButton>
            <Stack>
                <StyledPaper>
                    <Box
                        sx={{
                            p: 1,
                            position: 'relative',
                            height: '50px',
                            display: 'flex',
                            alignItems: 'center',
                            borderBottom: '1px solid #e0e0e0',
                        }}
                    >
                        <SearchIcon
                            sx={{
                                position: 'absolute',
                                insetInlineStart: '16px',
                                color: 'text.secondary',
                            }}
                        />
                        <StyledSearchInput
                            placeholder={t('saveJob.search')}
                            value={searchTerm}
                            onChange={handleSearchChange}
                        />
                    </Box>
                    <StyledList>
                        {options?.map((option) => {
                            const isSelected = requirementsArray.fields.some(
                                (item) =>
                                    String(item.requirementId) ===
                                    String(option.id)
                            );
                            return (
                                <ListItem
                                    key={option.id}
                                    onClick={() => handleOptionToggle(option)}
                                    sx={{
                                        cursor: 'pointer',
                                        height: '48px',
                                        '&:hover': {
                                            backgroundColor:
                                                'rgba(142, 36, 170, 0.04)',
                                        },
                                    }}
                                >
                                    <ListItemIcon>
                                        {isSelected ? (
                                            <CheckCircleIcon
                                                sx={{
                                                    color: theme.palette.primary
                                                        .main,
                                                }}
                                            />
                                        ) : (
                                            // ) : option.recommended ? (
                                            //     <ErrorOutlineIcon
                                            //         sx={{ color: '#4caf50' }}
                                            //     />
                                            <CircularAddIcon
                                                sx={{ color: '#bdbdbd' }}
                                            />
                                        )}
                                    </ListItemIcon>
                                    <ListItemText primary={option.name} />
                                </ListItem>
                            );
                        })}
                    </StyledList>
                    <Tooltip
                        title={
                            isRecommendationsExhausted
                                ? t(
                                      'saveJob.noMoreRecommendations',
                                      'No more requirements to recommend based on the job description and requirements provided'
                                  )
                                : ''
                        }
                        arrow
                    >
                        <span>
                            <StyledAddButton
                                disabled={
                                    isRecommendationsLoading ||
                                    areAllRecommendationsSelected ||
                                    isRecommendationsExhausted
                                }
                                onClick={handleAddRecommendationClicked}
                                startIcon={
                                    isRecommendationsLoading ? (
                                        <PargodaLoading
                                            height={24}
                                            width={24}
                                        />
                                    ) : (
                                        <ErrorOutlineIcon
                                            sx={{ fontSize: 20 }}
                                        />
                                    )
                                }
                            >
                                {isRecommendationsLoading
                                    ? t('saveJob.fetchingRecommendations')
                                    : t('saveJob.addRecomended')}
                            </StyledAddButton>
                        </span>
                    </Tooltip>
                </StyledPaper>
            </Stack>
        </Box>
    );
};

export default RequirementsSelector;
